import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, catchError, Observable, of, Subscription } from 'rxjs';
import { SearchCriteriaModel } from '@api/mentions/model/search-criteria.model';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { MonitoringBackEndDataSourceModel } from '../../Shared/models/monitoring-back-end-data-source.model';
import { MentionsApi } from '@api/mentions/mentions.api';
import { CoverageImage } from '../../Shared/models/coverage-image.model';
import { ImageHelper } from '@Common/PageBuilder/helpers/image.helper';
import { Mention } from '@Common/Mentions/models/mention';
import { MentionModel } from '@api/mentions/model/mention.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';

@Injectable()
export class CoverageImageService implements OnDestroy {
    private loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    loading$ = this.loadingSubject.asObservable();
    private coverageImagesSubject: BehaviorSubject<CoverageImage[]> = new BehaviorSubject<CoverageImage[]>([]);
    coverageImages$ = this.coverageImagesSubject.asObservable();
    private mentionCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    mentionCount$ = this.mentionCountSubject.asObservable();

    searchCriteria: SearchCriteriaModel;
    isSavedSource: boolean;
    imageUrls: string[]= [];
    protected subscriptions: Subscription[] = [];

    constructor(
        protected mentionsApi: MentionsApi
    ) {}

    searchCoverageImages() {
        this.loadingSubject.next(true);
        this.searchCriteria.page++;

        let searchRequest: Observable<MentionsSearchResponseModel>;
        if (this.isSavedSource) {
            searchRequest = this.mentionsApi.searchAccount(this.searchCriteria);
        } else {
            searchRequest = this.mentionsApi.searchMentions(this.searchCriteria);
        }

        searchRequest.pipe(
            catchError(() => {
                this.loadingSubject.next(false);
                return of(null);
            })
        ).subscribe(
            (response: MentionsSearchResponseModel) => {
                this.processMentionsToCoverageImages(response?.mentions);
            }
        );
    }

    updateSourceAndSearch(dataSource: MonitoringBackEndDataSourceModel): void {
        if (dataSource.combinedSearchCriteria) {
            this.searchCriteria = dataSource.combinedSearchCriteria.getApiEntity();
            this.searchCriteria.query.hasVisualMedia = true;
            // aim to get around 30 images per load
            this.searchCriteria.pageSize = 25;
            this.searchCriteria.page = 0;
        }

        this.isSavedSource = dataSource.isSavedSource();
        this.imageUrls = [];

        switch (dataSource.type) {
            case MonitoringSeriesTypeEnum.allCuratedCoverageInBrief:
                this.loadingSubject.next(true);
                this.processMentionsToCoverageImages(dataSource.mentions);
                break;
            case MonitoringSeriesTypeEnum.coverageInSection:
                if (dataSource.sourceSearchCriteria) {
                    this.searchCoverageImages();
                } else {
                    this.loadingSubject.next(true);
                    this.processMentionsToCoverageImages(dataSource.mentions);
                }
                break;
            default:
                this.searchCoverageImages();
        }
    }

    processMentionsToCoverageImages(mentions: MentionModel[]) {
        const newCoverageImages = [];

        mentions?.forEach(m => {
            m.images?.forEach(image => {
                const normalized = ImageHelper.normalizeImageUrl(image.url);
                if (!this.imageUrls.includes(normalized)) {
                    this.imageUrls.push(normalized);
                    const coverageImage = new CoverageImage();

                    coverageImage.name = ImageHelper.getImageNameFromUrl(image.url);
                    coverageImage.url = image.url;
                    coverageImage.mention = new Mention(m);
                    newCoverageImages.push(coverageImage);
                }
            });
        });

        this.mentionCountSubject.next(mentions?.length);
        this.coverageImagesSubject.next(newCoverageImages);
        this.loadingSubject.next(false);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
